import { useEffect, useState } from "react";
import axios from "axios";
import { Clock3, Droplets, Globe, Inbox, LogOut, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return iso; }
}

export default function AdminPage() {
  const [auth, setAuth] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [visitors, setVisitors] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/me`, { withCredentials: true })
      .then((r) => setAuth(r.data))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (!auth) return;
    Promise.all([
      axios.get(`${API}/admin/stats`, { withCredentials: true }),
      axios.get(`${API}/admin/enquiries`, { withCredentials: true }),
      axios.get(`${API}/admin/visitors`, { withCredentials: true }),
    ]).then(([s, e, v]) => {
      setStats(s.data); setEnquiries(e.data); setVisitors(v.data);
    }).catch(() => {});
  }, [auth]);

  const login = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      setAuth(data);
    } catch (err) {
      setError(fmtDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const [mode, setMode] = useState("password");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const sendOtp = async () => {
    setLoading(true); setError("");
    try { await axios.post(`${API}/admin/request-otp`, { email }); setOtpSent(true); }
    catch (err) { setError(fmtDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/admin/verify-otp`, { email, otp }, { withCredentials: true });
      setAuth(data);
    } catch (err) { setError(fmtDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); } catch {}
    setAuth(false);
  };

  if (auth === null) return <main className="admin-loading">Checking session…</main>;

  if (auth === false) {
    return (
      <main className="admin-page">
        <div className="admin-login" data-testid="admin-login-form">
          <span className="brand-mark"><Droplets size={19}/></span>
          <h1>Admin sign in</h1>
          <p>Parth Enterprises — enquiries & visitors</p>
          <div className="admin-tabs">
            <button type="button" data-testid="admin-tab-password" className={mode === "password" ? "active" : ""} onClick={() => { setMode("password"); setError(""); }}>Password</button>
            <button type="button" data-testid="admin-tab-otp" className={mode === "otp" ? "active" : ""} onClick={() => { setMode("otp"); setError(""); }}>Email OTP</button>
          </div>
          {mode === "password" ? (
          <form onSubmit={login}>
            <label>Email<input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"/></label>
            <label>Password<input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password"/></label>
            <button data-testid="admin-login-submit" className="btn btn-primary full" type="submit" disabled={loading}>{loading ? "Signing in…" : "Sign in"}</button>
            {error && <div data-testid="admin-login-error" className="form-note error">{error}</div>}
          </form>
          ) : (
          <form onSubmit={verifyOtp}>
            <label>Email<input data-testid="admin-otp-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email"/></label>
            {otpSent && <label>6-digit code<input data-testid="admin-otp-input" inputMode="numeric" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Check your Gmail"/></label>}
            {otpSent ? (
              <button data-testid="admin-otp-verify-button" className="btn btn-primary full" type="submit" disabled={loading}>{loading ? "Verifying…" : "Verify & sign in"}</button>
            ) : (
              <button data-testid="admin-otp-send-button" className="btn btn-primary full" type="button" onClick={sendOtp} disabled={loading}>{loading ? "Sending…" : "Send code to my email"}</button>
            )}
            {otpSent && <div data-testid="admin-otp-sent-message" className="form-note success">Code sent to your email — valid for 10 minutes.</div>}
            {error && <div data-testid="admin-login-error" className="form-note error">{error}</div>}
          </form>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="admin-page" data-testid="admin-dashboard">
      <div className="container">
        <div className="admin-head">
          <div><p className="kicker"><span/> Admin panel</p><h1>Namaste, Parth</h1></div>
          <button data-testid="admin-logout-button" className="btn btn-ghost" onClick={logout}><LogOut size={15}/> Log out</button>
        </div>

        <div className="admin-stats">
          <div className="stat-card" data-testid="stat-enquiries"><Inbox size={19}/><strong>{stats?.total_enquiries ?? "–"}</strong><span>Total enquiries</span></div>
          <div className="stat-card" data-testid="stat-visits"><Clock3 size={19}/><strong>{stats?.total_visits ?? "–"}</strong><span>Total visits</span></div>
          <div className="stat-card" data-testid="stat-unique"><Users size={19}/><strong>{stats?.unique_visitors ?? "–"}</strong><span>Unique visitors</span></div>
          <div className="stat-card" data-testid="stat-today"><Globe size={19}/><strong>{stats?.visits_today ?? "–"}</strong><span>Visits today</span></div>
        </div>

        <section className="admin-section">
          <h2>Enquiries</h2>
          <div className="table-wrap">
            <table className="admin-table" data-testid="enquiries-table">
              <thead><tr><th>Name</th><th>Phone</th><th>Service</th><th>Message</th><th>Received</th></tr></thead>
              <tbody>
                {enquiries.map((q) => (
                  <tr key={q.id}><td><b>{q.name}</b></td><td>{q.phone}</td><td>{q.service}</td><td>{q.message || "—"}</td><td>{fmtTime(q.created_at)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {enquiries.length === 0 && <p className="admin-empty">No enquiries yet.</p>}
        </section>

        <section className="admin-section">
          <h2>Visitors</h2>
          <div className="table-wrap">
            <table className="admin-table" data-testid="visitors-table">
              <thead><tr><th>IP address</th><th>Page</th><th>Time</th><th>Device</th></tr></thead>
              <tbody>
                {visitors.map((v, i) => (
                  <tr key={i}><td><b>{v.ip || "—"}</b></td><td>{v.path}</td><td>{fmtTime(v.created_at)}</td><td className="ua">{v.user_agent || "—"}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {visitors.length === 0 && <p className="admin-empty">No visits recorded yet.</p>}
        </section>
      </div>
    </main>
  );
}
